// A11y: while ANY scrimmed overlay (picker / modal / drawer / peek) is open, mark
// the persistent app landmarks behind it `inert` + `aria-hidden`. The focus trap
// only constrains Tab; a mobile screen-reader's virtual cursor (swipe - the dominant
// mobile-SR gesture) can otherwise read straight through the dialog into the content
// behind it.
//
// DOM-DERIVED, not ref-counted: the background is inert iff a scrim is currently in
// the DOM. That makes nesting (a peek over a drawer), drawer-switching, and the
// defensive no-op closes all resolve correctly with no counter to leak - every
// open/close just calls syncOverlayBackground() once it has added/removed its scrim.
// Callers must sync AFTER removing the scrim but BEFORE restoring focus to the opener
// (a background element), since real browsers block focus inside an inert subtree.
const SCRIM = ".scrim, .drawer-scrim, .peek-scrim, .gps-scrim";
const BG = ".topbar, .orient-slot, .content, .rail-journey, .rail-explore, .botnav-host, .foot, .desk-dock";

export function syncOverlayBackground(): void {
  const open = !!document.querySelector(SCRIM);
  for (const node of document.querySelectorAll<HTMLElement>(BG)) {
    if (open) {
      node.setAttribute("inert", "");
      node.setAttribute("aria-hidden", "true");
    } else {
      node.removeAttribute("inert");
      node.removeAttribute("aria-hidden");
    }
  }
}

// Cold remounts (a cross-parasha route()) reuse the SAME .content / .orient-slot via
// replaceChildren, but a mobile drawer/peek/modal mounts on document.body and survives
// that swap - so it would float over the fresh reader AND keep the reused landmarks
// inert+aria-hidden (sync only runs from an overlay's own close, which never fires on
// remount). Call this at the top of a remount to evict any stray overlay and clear the
// inert state. The owning navigator closure is discarded on remount, so removing the
// DOM is safe; its focus-trap handler lives on the removed element and can no longer fire.
const STRAY = ".scrim, .drawer-scrim, .peek-scrim, .gps-scrim, .picker, .journey-drawer, .peek-sheet, .gps-modal";
export function closeStrayOverlays(): void {
  for (const node of document.querySelectorAll(STRAY)) node.remove();
  syncOverlayBackground();
}
